import React from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

const HeroSection = ({ siteMeta, services }) => (
  <Box
    sx={{
      position: "relative",
      overflow: "hidden",
      color: "#fff",
      background:
        "radial-gradient(circle at top right, rgba(183,132,69,0.22), transparent 24%), linear-gradient(135deg, #0d1827 0%, #122135 42%, #1b2e48 100%)",
      pt: { xs: 8.5, md: 10.5, lg: 12.5 },
      pb: { xs: 8, md: 10, lg: 11.5 },
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)",
        backgroundSize: "96px 96px",
        opacity: 0.16,
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        opacity: 100,
        backgroundImage: `url(${siteMeta.heroImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center right",
        mixBlendMode: "screen",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        right: { lg: "-10%" },
        top: { lg: "-10%" },
        width: { lg: 560 },
        height: { lg: 560 },
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(184,134,70,0.2), transparent 68%)",
        pointerEvents: "none",
      }}
    />
    <Container sx={{ position: "relative", zIndex: 1 }}>
      <Grid
        container
        spacing={{ xs: 5, md: 6, lg: 8 }}
        alignItems="center"
        sx={{ width: "100%", m: 0 }}
      >
        <Grid item xs={12} lg={7}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            alignItems={{ sm: "center" }}
          >
            <Box
              className="eyebrow"
              sx={{
                color: "#fff",
                "&::before": {
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0.14), #dec095)",
                },
              }}
            >
              {siteMeta.projectName}
            </Box>
            <Chip
              label="Established Group"
              sx={{
                bgcolor: "rgba(184, 134, 70, 0.14)",
                color: "#fff",
                border: "1px solid rgba(216, 184, 131, 0.38)",
                width: "fit-content",
              }}
            />
          </Stack>
          <Typography
            variant="h2"
            sx={{
              mt: 2.6,
              maxWidth: 760,
              textWrap: "balance",
            }}
          >
            Industrial Capabilities
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 2.25,
              maxWidth: 660,
              color: "rgba(255,255,255,0.8)",
            }}
          >
            {siteMeta.tagline} Refwa supports industrial, commercial, and
            infrastructure requirements through established operations across
            core sectors and related project support services.
          </Typography>
          <Stack
            direction="row"
            spacing={1.1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 3 }}
          >
            {siteMeta.regions.map((region) => (
              <Box
                key={region}
                sx={{
                  px: 1.5,
                  py: 0.85,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.12)",
                  bgcolor: "rgba(255,255,255,0.05)",
                  color: "rgba(255,255,255,0.82)",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                }}
              >
                {region}
              </Box>
            ))}
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 3.75 }}
          >
            <Button
              component={RouterLink}
              to="/services"
              variant="contained"
              size="large"
              sx={{ minWidth: { sm: 188 } }}
            >
              Explore Services
            </Button>
            <Button
              component={RouterLink}
              to="/portfolio"
              variant="outlined"
              size="large"
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.26)",
                minWidth: { sm: 236 },
                bgcolor: "rgba(255,255,255,0.04)",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.06)",
                },
              }}
            >
              View Capabilities
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <Box
            sx={{
              position: "relative",
              maxWidth: 490,
              ml: { lg: "auto" },
              p: { xs: 2.7, md: 3.6 },
              borderRadius: "var(--radius-panel)",
              border: "1px solid rgba(255,255,255,0.14)",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.09), rgba(255,255,255,0.03))",
              backdropFilter: "blur(20px)",
              boxShadow: "var(--shadow-hero)",
              overflow: "hidden",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "auto -12% -22% auto",
                width: 220,
                height: 220,
                background:
                  "radial-gradient(circle, rgba(184,134,70,0.22), rgba(184,134,70,0.08) 46%, transparent 70%)",
                opacity: 0.9,
                pointerEvents: "none",
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{ color: "primary.light", position: "relative", zIndex: 1 }}
            >
              Core Sectors
            </Typography>
            <Typography
              variant="h4"
              sx={{ mt: 1.2, maxWidth: 360, position: "relative", zIndex: 1 }}
            >
              Mining, petroleum, and trade.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                color: "rgba(255,255,255,0.72)",
                maxWidth: 372,
                position: "relative",
                zIndex: 1,
              }}
            >
              Refwa's operating range also includes construction, industrial
              support, equipment services, environmental work, and
              sustainability-related activity.
            </Typography>
            <Grid
              container
              spacing={1.5}
              sx={{
                mt: 1.6,
                position: "relative",
                zIndex: 1,
                width: "100%",
                m: 0,
              }}
            >
              {services.slice(0, 6).map((service) => (
                <Grid
                  key={service.id}
                  item
                  xs={12}
                  sm={6}
                  sx={{ display: "flex" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      p: 1.85,
                      borderRadius: 0.5,
                      backgroundColor: "rgba(8,14,24,0.34)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, color: "#fff" }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.55,
                        color: "rgba(255,255,255,0.68)",
                        textTransform: "uppercase",
                        letterSpacing: "0.09em",
                      }}
                    >
                      Operating area
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default HeroSection;
